import { useQuery } from '@tanstack/react-query';

import { authQuery } from '@/lib/client/auth-query';
import { FeatureAccessResult, FeatureName } from '@/lib/feature-access';
import { queryKeys } from '@/constants/query-keys';

/**
 * Hook to get feature access based on subscription history
 * Returns: accessible (active), view (had subscription), hidden (never had)
 */export const useSubscriptionFeatureAccess = () => {
  return useQuery({
    queryKey: [queryKeys.featureAccess],
    queryFn: authQuery(async ({ supabase, user }) => {
      let targetUserId = user.id;
      
      // Check if current user is an agency member
      const { data: membershipData, error: membershipError } = await supabase
        .from('agency_members')
        .select(`
          agency_id,
          agencies!inner(
            created_by
          )
        `)
        .eq('user_id', user.id)
        .eq('role', 'member')
        .maybeSingle();

      if (membershipError && membershipError.code !== 'PGRST116') {
        throw new Error(membershipError.message);
      }

      // If user is an agency member, use the owner's ID for subscription check
      if (membershipData?.agencies) {
        const agency = membershipData.agencies as { created_by: string };
        targetUserId = agency.created_by;
      }

      // Get all features first
      const { data: allFeatures, error: featuresError } = await supabase
        .from('features')
        .select('name')
        .eq('is_active', true);

      if (featuresError) throw new Error(featuresError.message);

      // Get target user's subscription history for all features
      const { data: subscriptionData, error: subscriptionError } =
        await supabase
          .from('user_subscriptions')
          .select(`
            plan_id,
            status,
            current_period_end,
            cancel_at_period_end,
            plans!inner(
              single_feature_id,
              plan_features_limits!inner(
                feature_id,
                features!inner(name)
              )
            )
          `)
          .eq('user_id', targetUserId);

      if (subscriptionError) throw new Error(subscriptionError.message);

      // Process the data to determine access level for each feature
      const featureAccess: Record<FeatureName, FeatureAccessResult> =
        {} as Record<FeatureName, FeatureAccessResult>;

      // Initialize all features as hidden
      allFeatures?.forEach((feature) => {
        const featureName = feature.name as FeatureName;
        featureAccess[featureName] = {
          accessLevel: 'hidden',
          hasAccess: false,
          canView: false,
          isHidden: true,
        };
      });

      // Track subscription status for each feature
      const featureSubscriptionStatus: Record<
        string,
        { hasActive: boolean; hasHistory: boolean }
      > = {};

      // Process subscription data
      for (const subscription of subscriptionData || []) {
        const plan = subscription.plans as {
          single_feature_id: string | null;
          plan_features_limits: Array<{
            features: {
              name: string;
            };
          }>;
        };

        const currentPeriodEnd = subscription.current_period_end
          ? new Date(subscription.current_period_end)
          : null;
        const now = new Date();
        const isActive =
          (subscription.status === 'active' || subscription.status === 'trialing') &&
          (!currentPeriodEnd || currentPeriodEnd > now);

        // Process plan feature limits
        plan.plan_features_limits?.forEach((limit) => {
          const featureName = limit.features.name as FeatureName;

          if (!featureName || !featureAccess.hasOwnProperty(featureName))
            return;

          // Initialize tracking for this feature if not exists
          if (!featureSubscriptionStatus[featureName]) {
            featureSubscriptionStatus[featureName] = {
              hasActive: false,
              hasHistory: false,
            };
          }

          // Mark that user has subscription history for this feature
          featureSubscriptionStatus[featureName].hasHistory = true;

          // Mark if user has active subscription for this feature
          if (isActive) {
            featureSubscriptionStatus[featureName].hasActive = true;
          }
        });

        // Handle single feature plans
        if (plan.single_feature_id) {
          const { data: singleFeature, error: singleFeatureError } =
            await supabase
              .from('features')
              .select('name')
              .eq('id', plan.single_feature_id)
              .single();

          if (!singleFeatureError && singleFeature) {
            const featureName = singleFeature.name as FeatureName;

            if (featureAccess.hasOwnProperty(featureName)) {
              // Initialize tracking for this feature if not exists
              if (!featureSubscriptionStatus[featureName]) {
                featureSubscriptionStatus[featureName] = {
                  hasActive: false,
                  hasHistory: false,
                };
              }

              // Mark that user has subscription history for this feature
              featureSubscriptionStatus[featureName].hasHistory = true;

              // Mark if user has active subscription for this feature
              if (isActive) {
                featureSubscriptionStatus[featureName].hasActive = true;
              }
            }
          }
        }
      }

      // Apply the final access levels based on subscription status
      Object.entries(featureSubscriptionStatus).forEach(
        ([featureName, status]) => {
          const feature = featureName as FeatureName;

          if (status.hasActive) {
            // User has active subscription - full access
            featureAccess[feature] = {
              accessLevel: 'accessible',
              hasAccess: true,
              canView: true,
              isHidden: false,
            };
          } else if (status.hasHistory) {
            // User had subscription before but not active - view only
            featureAccess[feature] = {
              accessLevel: 'view',
              hasAccess: false,
              canView: true,
              isHidden: false,
            };
          }
          // If neither hasActive nor hasHistory, feature remains 'hidden' (already initialized)
        },
      );
      return featureAccess;
    }),
  });
};

/**
 * Hook to check access for a specific feature based on subscription history
 */
export const useSubscriptionFeatureAccessForFeature = (
  feature: FeatureName,
) => {
  const {
    data: allFeatureAccess,
    isLoading,
    error,
  } = useSubscriptionFeatureAccess();

  return {
    data: allFeatureAccess
      ? allFeatureAccess[feature]
      : {
          accessLevel: 'hidden' as const,
          hasAccess: false,
          canView: false,
          isHidden: true,
        },
    isLoading,
    error,
  };
};
